"use client";

import { Button } from "@/components/ui/button";
import { Career } from "@/lib/types/career";
import { Story } from "@/lib/types/story";
import { ArrowLeft, FileText, Share2 } from "lucide-react";
import Link from "next/link";
import HtmlPreviewer from "./RichContent/html-previewer";
import { Card, CardContent } from "@/components/ui/card";
import { useEffect, useState } from "react";
import { api } from "@/lib/fetcher";
import { ApiResponse } from "@/lib/types";
import Image from "next/image";
import { getFileUrl } from "@/lib/file.util";
import ShareButton from "./share-button";

interface ContentDetailProps {
  career?: Career;
  story: Story;
}

export default function ContentDetailView({
  career,
  story,
}: ContentDetailProps) {
  const [suggestedStories, setSuggestedStories] = useState<Story[]>([]);
  const [currentUrl, setCurrentUrl] = useState('');

  useEffect(() => {
    setCurrentUrl(window.location.href);
  }, []);

  useEffect(() => {
    const fetchSuggestedStories = async () => {
      try {
        const response = await api.get<ApiResponse<Story[]>>(`/story/suggested/${story.id}`);
        setSuggestedStories(response.data?.data || []);
      } catch (error) {
        console.error("Error fetching suggested stories:", error);
      }
    };

    fetchSuggestedStories();
  }, [story.id]);

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex flex-col lg:flex-row w-fit mx-auto gap-8">
        {/* Main Content */}
        <div className="flex-1 max-w-3xl">
          {career && (
            <div className="mb-8">
              <Link
                href={`/careers/${career.id}`}
                className="inline-flex items-center text-gray-600 hover:text-green-600 transition-colors"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to {career.title} Journey
              </Link>
            </div>
          )}

          <div className="mb-6">
            <div className="flex items-center gap-2 text-sm text-gray-500 mb-2">
              {<FileText className="h-4 w-4 text-green-600" />}
              {/* {content.type === "text" && <FileText className="h-4 w-4 text-green-600" />}
              {content.type === "video" && <Video className="h-4 w-4 text-blue-600" />}
              {content.type === "audio" && <Headphones className="h-4 w-4 text-purple-600" />} */}
              {/* <span className="capitalize">{content.type}</span> */}
              <span>•</span>
              <span>
                {new Date(story.createdAt).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "short",
                  day: "numeric",
                })}
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              {story.title}
            </h1>
            {/* Image */}
            <div className="relative aspect-video rounded-lg overflow-hidden mb-6">
              <Image
                src={getFileUrl(story.thumbnail)}
                alt={story.title}
                fill
                className="object-cover"
              />
            </div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-10 w-10 aspect-square rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                {story.title.charAt(0)}
              </div>
              <div>
                <div className="font-medium">{story.title}</div>
                <div className="text-sm text-gray-500">{story.description}</div>
              </div>
            </div>
          </div>
          {story && <HtmlPreviewer content={story.content} />}
          <div className="mt-8 pt-6 border-t">
            <ShareButton
              title={story.title}
              url={currentUrl}
              description={story.description}
            >
              Share this story
            </ShareButton>
          </div>
        </div>

        {/* Right Panel - Suggested Stories */}
        <div className="lg:w-80 flex-shrink-0">
          <div className="sticky top-20">
            <h2 className="text-xl font-semibold mb-4">Suggested Stories</h2>
            <div className="space-y-4">
              {suggestedStories.map((suggestedStory) => (
                <Link href={`/stories/${suggestedStory.id}`} key={suggestedStory.id}>
                  <Card className="overflow-hidden hover:border-primary hover:shadow-md transition-all duration-200">
                    <div className="aspect-video relative">
                      <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{ backgroundImage: `url(${suggestedStory.thumbnail})` }}
                      />
                      <div className="absolute inset-0 bg-black/30" />
                    </div>
                    <CardContent className="p-3">
                      <h3 className="font-medium text-sm mb-1 line-clamp-2">
                        {suggestedStory.title}
                      </h3>
                      <p className="text-gray-600 text-xs line-clamp-2">
                        {suggestedStory.description}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
            <div className="mt-6">
              <Link href="/stories">
                <Button variant="outline" className="w-full">
                  View All Stories
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
